import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/homeData";

export default function Footer() {
  const { t } = useTranslation();
  const footerCategories = categories.slice(0, 5);
  const [email, setEmail] = useState("");
  const [submitStatus, setSubmitStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem("website_alt") as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitStatus("success");
      return;
    }
    setSubmitStatus("loading");
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      await fetch("https://readdy.ai/api/form/d8um131l2p03kijan4h0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setSubmitStatus("success");
      setEmail("");
    } catch {
      setSubmitStatus("error");
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-50 relative overflow-hidden">
      {/* Decorative map outline */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div className="w-full h-full flex items-center justify-center text-[600px] font-light text-background-50 select-none">
          🌐
        </div>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-4 md:px-8 py-16 md:py-20">
        {/* Upper Footer */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand + Newsletter */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block cursor-pointer">
              <span className="font-heading text-2xl font-semibold text-background-50">
                hoobuy<span className="text-primary-400">.</span>
              </span>
            </Link>

            {/* Newsletter */}
            <form
              onSubmit={handleNewsletterSubmit}
              data-readdy-form
              className="mt-6 flex items-center gap-2 max-w-sm"
            >
              <div className="flex-1 relative">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder={t("footer.emailPlaceholder")}
                  className="w-full bg-foreground-800 border border-foreground-700 rounded-xl px-4 py-3 text-sm text-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hp-field"
              />
              <button
                type="submit"
                disabled={submitStatus === "loading"}
                className="w-11 h-11 rounded-xl bg-primary-500 flex items-center justify-center cursor-pointer hover:bg-primary-400 transition-colors shrink-0 disabled:opacity-60"
                aria-label="Subscrever newsletter"
              >
                <i className="ri-mail-line text-background-50 text-lg"></i>
              </button>
            </form>
            {submitStatus === "success" && (
              <p className="text-xs text-accent-400 mt-2">Subscrição confirmada! Obrigado.</p>
            )}
            {submitStatus === "error" && (
              <p className="text-xs text-red-400 mt-2">Erro ao subscrever. Tente novamente.</p>
            )}

            <p className="mt-6 font-heading text-xl md:text-2xl text-background-50 leading-snug">
              {t("footer.tagline1")}
              <br />
              {t("footer.tagline2")}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-300 uppercase tracking-wider mb-4">
              {t("footer.navigation")}
            </h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/" className="text-sm text-background-50/70 hover:text-background-50 transition-colors">
                  {t("footer.home")}
                </Link>
              </li>
              <li>
                <a href="#categories" className="text-sm text-background-50/70 hover:text-background-50 transition-colors">
                  {t("footer.categoriesLink")}
                </a>
              </li>
              <li>
                <Link to="/artigo/guia-completo-hoobuyspreadsheet" className="text-sm text-background-50/70 hover:text-background-50 transition-colors">
                  {t("footer.blog")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-300 uppercase tracking-wider mb-4">
              {t("common.categories")}
            </h4>
            <ul className="space-y-2.5">
              {footerCategories.map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-background-50/70 hover:text-background-50 transition-colors"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support + Social */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-300 uppercase tracking-wider mb-4">
              {t("footer.support")}
            </h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/artigo/guia-completo-hoobuyspreadsheet" className="text-sm text-background-50/70 hover:text-background-50 transition-colors">
                  {t("footer.safety")}
                </Link>
              </li>
              <li>
                <a href="#faq" className="text-sm text-background-50/70 hover:text-background-50 transition-colors">
                  {t("footer.faq")}
                </a>
              </li>
              <li>
                <span className="text-sm text-background-50/50 cursor-default">{t("footer.terms")}</span>
              </li>
            </ul>

            <h4 className="text-xs font-semibold text-foreground-300 uppercase tracking-wider mt-6 mb-4">
              {t("footer.social")}
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a
                  href="https://tiktok.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-background-50/70 hover:text-background-50 transition-colors"
                >
                  TikTok
                </a>
              </li>
              <li>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-background-50/70 hover:text-background-50 transition-colors"
                >
                  Instagram
                </a>
              </li>
              <li>
                <a
                  href="https://pinterest.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-background-50/70 hover:text-background-50 transition-colors"
                >
                  Pinterest
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-14 pt-8 border-t border-foreground-800/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xs text-foreground-400">
            {t("footer.copyright")}
          </span>

          {/* Social Icons */}
          <div className="flex items-center gap-3">
            {[
              { icon: "ri-tiktok-line", url: "https://tiktok.com" },
              { icon: "ri-instagram-line", url: "https://instagram.com" },
              { icon: "ri-twitter-x-line", url: "https://x.com" },
              { icon: "ri-youtube-line", url: "https://youtube.com" },
            ].map((social) => (
              <a
                key={social.icon}
                href={social.url}
                target="_blank"
                rel="noopener nofollow"
                className="w-9 h-9 rounded-lg border border-foreground-700 flex items-center justify-center cursor-pointer hover:border-background-50/50 transition-colors"
                aria-label={social.icon}
              >
                <i className={`${social.icon} text-background-50/60 text-sm hover:text-background-50 transition-colors`}></i>
              </a>
            ))}
          </div>

          <span className="text-xs text-foreground-400">
            {t("footer.privacy")}
          </span>
        </div>
      </div>
    </footer>
  );
}